import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeSalespersonPerformanceModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_SALES = "#5CC7C1";
const COLOR_QTY = "#5C74C7";

/** Vertical band behind hovered category (Figma column highlight). */
const salespersonHoverBandPlugin = {
  id: "salespersonHoverBand",
  beforeDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const idx = active[0].index;
    const meta = chart.getDatasetMeta(0);
    const bar = meta.data[idx];
    if (!bar || bar.skip) return;
    const { x, width } = bar.getProps(["x", "width"], true);
    if (x == null || width == null) return;
    const pad = 6;
    const { ctx, chartArea } = chart;
    const left = Math.max(chartArea.left, x - width / 2 - pad);
    const right = Math.min(chartArea.right, x + width / 2 + pad);
    const w = right - left;
    ctx.save();
    ctx.fillStyle = "rgba(242, 242, 242, 0.92)";
    ctx.fillRect(left, chartArea.top, w, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
};

function formatSalesAxisTick(value) {
  const v = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

/** ~4–6 ticks on Sales axis — avoids crowded 120k steps when totals are ~2M. */
function salesAxisMaxAndStep(maxSales) {
  const m = typeof maxSales === "number" ? maxSales : Number(maxSales);
  const peak = Number.isFinite(m) && m > 0 ? m : 0;
  if (peak <= 0) {
    return { max: 200000, step: 50000 };
  }
  const padded = peak * 1.12;
  const targetTicks = 5;
  const rawStep = padded / targetTicks;
  const pow10 = 10 ** Math.floor(Math.log10(rawStep));
  const n = rawStep / pow10;
  let nice = 10;
  if (n <= 1) nice = 1;
  else if (n <= 2) nice = 2;
  else if (n <= 5) nice = 5;
  const step = nice * pow10;
  const max = Math.ceil(padded / step) * step;
  return { max, step };
}

function formatTooltipSales(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatTableSales(n) {
  return formatTooltipSales(n);
}

function formatTableQty(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return String(Math.round(v));
}

/**
 * @param {import("./dashboardChartsModel.js").SalespersonPerformanceModel} model
 * @param {(el: HTMLElement | null) => void} getTooltipHost
 * @param {(key: string, opts?: object) => string} t
 */
function createExternalTooltipHandler(model, getTooltipHost, t) {
  return (context) => {
    const { chart, tooltip } = context;
    const host = getTooltipHost() ?? chart.canvas.parentNode;
    if (!host) return;

    let el = host.querySelector(".pos-salesperson-perf-tooltip");
    if (!el) {
      el = document.createElement("div");
      el.className = "pos-salesperson-perf-tooltip";
      el.setAttribute("role", "tooltip");
      host.appendChild(el);
    }

    if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      return;
    }

    const idx = tooltip.dataPoints[0].dataIndex;
    const name = String(model.names[idx] ?? "");
    const salesVal = model.sales[idx] ?? 0;
    const qtyVal = model.qty[idx] ?? 0;

    const salesLabel = t("Sales", { defaultValue: "Sales" });
    const qtyLabel = t("Qty", { defaultValue: "Qty" });

    el.innerHTML = `
      <div class="pos-salesperson-perf-tooltip__header"></div>
      <div class="pos-salesperson-perf-tooltip__body">
        <div class="pos-salesperson-perf-tooltip__row">
          <span class="pos-salesperson-perf-tooltip__icon pos-salesperson-perf-tooltip__icon--dot" style="background:${COLOR_SALES}" aria-hidden="true"></span>
          <span class="pos-salesperson-perf-tooltip__label">${salesLabel}</span>
          <strong class="pos-salesperson-perf-tooltip__value">${formatTooltipSales(salesVal)}</strong>
        </div>
        <div class="pos-salesperson-perf-tooltip__row">
          <span class="pos-salesperson-perf-tooltip__icon pos-salesperson-perf-tooltip__icon--line" aria-hidden="true"></span>
          <span class="pos-salesperson-perf-tooltip__label">${qtyLabel}</span>
          <strong class="pos-salesperson-perf-tooltip__value">${formatTableQty(qtyVal)}</strong>
        </div>
      </div>`;
    const head = el.querySelector(".pos-salesperson-perf-tooltip__header");
    if (head) head.textContent = name;

    const canvas = chart.canvas;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + tooltip.caretX;
    const cy = rect.top + tooltip.caretY;
    el.style.position = "fixed";
    el.style.left = `${cx}px`;
    el.style.top = `${cy}px`;
    el.style.transform = "translate(-50%, calc(-100% - 10px))";
    el.style.opacity = "1";
    el.style.pointerEvents = "none";
    el.style.zIndex = "20";
  };
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").SalespersonPerformanceModel }} props
 */
function SalespersonPerformanceTable({ model }) {
  const { t } = useTranslation();
  const baseRows = useMemo(() => {
    const names = model?.names ?? [];
    return names.map((name, i) => ({
      key: `${name}-${i}`,
      name,
      sales: model.sales[i] ?? 0,
      qty: model.qty[i] ?? 0,
    }));
  }, [model]);

  const [sort, setSort] = useState(() => ({
    key: /** @type {"name"|"sales"|"qty"} */ ("sales"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "sales", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "name":
          c = String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" });
          break;
        case "sales":
          c = a.sales - b.sales;
          break;
        case "qty":
          c = a.qty - b.qty;
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const thBase = "pos-salesperson-perf-table__th";
  const thSorted = "pos-salesperson-perf-table__th--sorted";

  return (
    <div
      className="pos-salesperson-perf-table"
      role="table"
      aria-label={t("graphSalepersonPerformance", { defaultValue: "Sale Person Performance" })}
    >
      <div className="pos-salesperson-perf-table__head" role="rowgroup">
        <div className="pos-salesperson-perf-table__tr pos-salesperson-perf-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-salesperson-perf-table__th--name"
            ariaSortLabel={t("Sort by sale person", { defaultValue: "Sort by sale person" })}
            sorted={sort.key === "name"}
            direction={sort.dir}
            onSort={() => toggleSort("name")}
          >
            {t("salePersonColumn", { defaultValue: "Sale person" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-salesperson-perf-table__th--num"
            ariaSortLabel={t("Sort by sales", { defaultValue: "Sort by sales" })}
            sorted={sort.key === "sales"}
            direction={sort.dir}
            onSort={() => toggleSort("sales")}
          >
            {t("Sales", { defaultValue: "Sales" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-salesperson-perf-table__th--num"
            ariaSortLabel={t("Sort by quantity", { defaultValue: "Sort by quantity" })}
            sorted={sort.key === "qty"}
            direction={sort.dir}
            onSort={() => toggleSort("qty")}
          >
            {t("Qty", { defaultValue: "Qty" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-salesperson-perf-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-salesperson-perf-table__tr" role="row">
            <div className="pos-salesperson-perf-table__td pos-salesperson-perf-table__td--name" role="cell">
              {row.name}
            </div>
            <div className="pos-salesperson-perf-table__td pos-salesperson-perf-table__td--num" role="cell">
              {formatTableSales(row.sales)}
            </div>
            <div className="pos-salesperson-perf-table__td pos-salesperson-perf-table__td--num" role="cell">
              {formatTableQty(row.qty)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Saleperson performance — combo chart (Sales bars + Qty line) and table; kebab Chart / Table / Download / Remove.
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").SalespersonPerformanceModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function SalespersonPerformanceChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const chartWrapRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewMode, setViewMode] = useState(/** @type {"chart"|"table"} */ ("chart"));
  const merged = useMemo(() => mergeSalespersonPerformanceModel(model ?? null), [model]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("saleperson-performance"));
  }, []);

  const getTooltipHost = useCallback(() => chartWrapRef.current, []);

  const { chartData, options } = useMemo(() => {
    const labels = merged.names ?? [];
    const sales = merged.sales ?? [];
    const qty = merged.qty ?? [];
    const labelCount = labels.length;
    const maxSales = sales.reduce((acc, v) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(acc, n) : acc;
    }, 0);
    const { max: salesAxisMax, step: salesAxisStep } = salesAxisMaxAndStep(maxSales);

    const externalTooltip = createExternalTooltipHandler(merged, getTooltipHost, t);

    const data = {
      labels,
      datasets: [
        {
          type: "bar",
          label: t("Sales", { defaultValue: "Sales" }),
          data: sales,
          backgroundColor: COLOR_SALES,
          yAxisID: "y1",
          order: 2,
          maxBarThickness: 32,
          borderRadius: 0,
          borderSkipped: false,
          pointStyle: "rectRounded",
        },
        {
          type: "line",
          label: t("Qty", { defaultValue: "Qty" }),
          data: qty,
          borderColor: COLOR_QTY,
          backgroundColor: COLOR_QTY,
          tension: 0,
          pointStyle: "circle",
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBorderWidth: 0,
          pointBackgroundColor: COLOR_QTY,
          borderWidth: 2,
          yAxisID: "y",
          order: 0,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: externalTooltip,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            maxRotation: labelCount > 6 ? 45 : 0,
            minRotation: 0,
            autoSkip: labelCount > 16,
          },
        },
        y: {
          position: "left",
          suggestedMax: 60,
          title: {
            display: true,
            text: t("Qty", { defaultValue: "Qty" }),
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          ticks: {
            stepSize: 15,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: { display: false },
        },
        y1: {
          position: "right",
          min: 0,
          max: salesAxisMax,
          title: {
            display: true,
            text: t("Sales", { defaultValue: "Sales" }),
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          grid: { drawOnChartArea: false },
          ticks: {
            stepSize: salesAxisStep,
            maxTicksLimit: 8,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => formatSalesAxisTick(v),
          },
          border: { display: false },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [merged, t, getTooltipHost]);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--salesperson-perf${
        viewMode === "table" ? " pos-dashboard-chart-card--salesperson-perf-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">
          {t("graphSalepersonPerformance", { defaultValue: "Sale Person Performance" })}
        </h2>
        <DashboardChartCardKebabMenu
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onDownload={handleDownloadPng}
          onRemove={onMenuRemove}
          showViewToggle
        />
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--salesperson-perf${
          viewMode === "table" ? " pos-dashboard-chart-card__body--salesperson-perf-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--salesperson-perf">
          {viewMode === "table" ? (
            <SalespersonPerformanceTable model={merged} />
          ) : (
            <>
              <div ref={chartWrapRef} className="pos-dashboard-chart-card__canvas pos-dashboard-chart-card__canvas--salesperson-perf">
                <Chart type="bar" data={chartData} options={options} plugins={[salespersonHoverBandPlugin]} />
              </div>
              <div
                className="pos-salesperson-perf-legend"
                role="list"
                aria-label={t("Salesperson performance legend", { defaultValue: "Salesperson performance legend" })}
              >
                <div className="pos-salesperson-perf-legend__item" role="listitem">
                  <span className="pos-salesperson-perf-legend__sales-dot" aria-hidden />
                  <span>{t("Sales", { defaultValue: "Sales" })}</span>
                </div>
                <div className="pos-salesperson-perf-legend__item" role="listitem">
                  <span className="pos-salesperson-perf-legend__qty-line-circle" aria-hidden />
                  <span>{t("Qty", { defaultValue: "Qty" })}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SalespersonPerformanceChartCard;
